import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import MuiTabs from '../common/MuiTabs';
import { ChangeEvent, useState, memo, useEffect, createContext, useContext } from "react";
import Box from '@mui/material/Box';
import { IconButton, Popover, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { Orders } from "./Order";
import { Position } from "./Position";
import { Logs } from './Logs';
import { TradeLogs } from './TradeLogs';
import ExecutionHistory from './ExecutionHistory';
import ExecutionMetrics from './ExecutionMetrics'; // NEW: ExecutionMetrics component (isolated)
import BarChartIcon from '@mui/icons-material/BarChart';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BuildIcon from '@mui/icons-material/Build';
import HistoryIcon from '@mui/icons-material/History';
import SpeedIcon from '@mui/icons-material/Speed'; // NEW: Icon for ExecutionMetrics tab

// Create a context for sharing tab state
const TabContext = createContext();

// Global state manager
const tabStateManager = {
    selectedTab: 0,
    listeners: [],
    
    setSelectedTab(value) {
        this.selectedTab = value;
        this.listeners.forEach(listener => listener(value));
    },
    
    subscribe(listener) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }
};

/**
 * OrderMonitor Component
 *
 * @param {object} props
 * @param {boolean} props.tabsOnly - Render only tabs (no content)
 * @param {boolean} props.contentOnly - Render only content (no tabs)
 * @param {object|string|number|null} props.selectedAccount - Account to filter by
 */
export const OrderMonitor = memo(({ tabsOnly = false, contentOnly = false, selectedAccount = null }) => {
    const [selectedTab, setSelectedTab] = useState(tabStateManager.selectedTab);
    const [positionSettingsAnchor, setPositionSettingsAnchor] = useState(null);
    const [columnVisibility, setColumnVisibility] = useState({
        id: true,
        symbol: true,
        direction: true,
        quantity: true,
        avgOpenPrice: true,
        currentPrice: true,
        profit: true,
        profitPips: true
    });
    const tabsLabels = [
        { label: "Positions", icon: <BarChartIcon sx={{mr:1}} fontSize="medium" /> },
        { label: "Execution Metrics", icon: <SpeedIcon sx={{mr:1}} fontSize="medium" /> }, // NEW: ExecutionMetrics tab (inserted at position 1)
        { label: "Execution History", icon: <HistoryIcon sx={{mr:1}} fontSize="medium" /> },
        { label: "Orders", icon: <AssignmentIcon sx={{mr:1}} fontSize="medium" /> },
        { label: "Trade Logs", icon: <ShowChartIcon sx={{mr:1}} fontSize="medium" /> },
        { label: "FIX Logs", icon: <BuildIcon sx={{mr:1}} fontSize="medium" /> }
    ];
    
    // Subscribe to global state changes
    useEffect(() => {
        const unsubscribe = tabStateManager.subscribe((newTab) => {
            setSelectedTab(newTab);
        });
        return unsubscribe;
    }, []);
    
    // Data recovery on mount
    useEffect(() => {
    }, []);
    
    const handleRefreshClick = () => {};
    const handleChange = (value) => {
        tabStateManager.setSelectedTab(value);
        // Close any open dropdowns when tab changes
        if (window.closeAllDropdowns) {
            window.closeAllDropdowns();
        }
    };

    // Column settings handlers
    const handleColumnSettingsClose = () => {
        setPositionSettingsAnchor(null);
    };

    const handleColumnToggle = (columnKey) => {
        setColumnVisibility(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey]
        }));
    };

    // If tabsOnly is true, just return the tabs
    if (tabsOnly) {
        return (
            <MuiTabs
                tabData={tabsLabels}
                variant="fullWidth"
                value={selectedTab}
                showRefresh={false}
                handleChange={handleChange}
                handleRefreshClick={handleRefreshClick}
                isHeaderTabs={true}
            />
        );
    }

    // If contentOnly is true, just return the content
    if (contentOnly) {
        return (
            <Box
                p={0}
                display="flex"
                flexDirection="column"
                gap={0}
                position="relative"
            >
                {selectedTab === 0 && <Position selectedAccount={selectedAccount} />}
                {selectedTab === 1 && <ExecutionMetrics />} {/* NEW: ExecutionMetrics tab content */}
                {selectedTab === 2 && <ExecutionHistory />}
                {selectedTab === 3 && <Orders />}
                {selectedTab === 4 && <TradeLogs />}
                {selectedTab === 5 && <Logs />}
            </Box>
        );
    }
    
    return(
    <>
        <Box
            p={0}
            display="flex"
            flexDirection="column"
            gap={0}
            position="relative"
            sx={{ height: '100%' }}
        >
            <MuiTabs
                tabData={tabsLabels}
                variant="fullWidth"
                value={selectedTab}
                showRefresh={false}
                handleChange={handleChange}
                handleRefreshClick={handleRefreshClick}
                isHeaderTabs={false}
            />

                        {selectedTab === 0 &&(
                            <Position selectedAccount={selectedAccount} />
                        )}
                {selectedTab === 1 &&( /* NEW: ExecutionMetrics tab content */
                    <ExecutionMetrics/>
                )}
                {selectedTab === 2 &&(
                    <ExecutionHistory/>
                )}
                {selectedTab === 3 &&(
                    <Orders/>
                )}
                {selectedTab === 4 &&(
                    <TradeLogs />
                )}
                {selectedTab === 5 &&(
                    <Logs/>
                )}
        </Box>
        
        {/* Column Settings Popover for Positions */}
        <Popover
            open={Boolean(positionSettingsAnchor)}
            anchorEl={positionSettingsAnchor}
            onClose={handleColumnSettingsClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                sx: {
                    backgroundColor: '#2a2a2a',
                    color: 'white',
                    borderRadius: '8px',
                    border: '1px solid rgba(6, 182, 212, 0.3)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    minWidth: 200,
                    maxHeight: 400,
                    overflow: 'auto'
                }
            }}
        >
            <List sx={{ padding: 0 }}>
                {/* Column Options */}
                {[
                    { key: 'id', label: 'ID' },
                    { key: 'symbol', label: 'Symbol' },
                    { key: 'direction', label: 'Direction' },
                    { key: 'quantity', label: 'Quantity' },
                    { key: 'avgOpenPrice', label: 'AVG Open Price' },
                    { key: 'currentPrice', label: 'Current Price' },
                    { key: 'profit', label: 'Profit' },
                    { key: 'profitPips', label: 'Profit Pips' }
                ].map((column) => (
                    <ListItem
                        key={column.key}
                        button
                        onClick={() => handleColumnToggle(column.key)}
                        sx={{
                            padding: '8px 16px',
                            '&:hover': {
                                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                            }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            {columnVisibility[column.key] ? (
                                <Typography sx={{ color: 'white', fontSize: '14px' }}>✓</Typography>
                            ) : (
                                <Typography sx={{ color: 'transparent', fontSize: '14px' }}>✓</Typography>
                            )}
                        </ListItemIcon>
                        <ListItemText 
                            primary={column.label}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: 400
                                }
                            }}
                        />
                    </ListItem>
                ))}
            </List>
        </Popover>
    </>
    );
});